import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Divider,
  Box,
  Spinner,
  ProgressBar,
} from '@shopify/polaris';
import { api } from '../utils/api';

interface Plan {
  id: string;
  name: string;
  price: number;
  shipmentsLimit: number;
  features: string[];
  popular?: boolean;
}

// Interface matching API response
interface SubscriptionStatus {
  planId: string;
  planName: string;
  status: string;
  shipmentsThisMonth: number;
  shipmentsLimit: number;
  trialDaysRemaining?: number;
  nextBillingDate?: string;
}

export default function Billing() {
  const { data: status, isLoading: statusLoading } = useQuery<SubscriptionStatus>({
    queryKey: ['billing-status'],
    queryFn: () => api.get('/billing/status'),
  });

  const { data: plans } = useQuery<Plan[]>({
    queryKey: ['billing-plans'],
    queryFn: () => api.get('/billing/plans'),
  });

  const subscribeMutation = useMutation({
    mutationFn: (planId: string) => api.post('/billing/subscribe', { planId }),
    onSuccess: (data: any) => {
      if (data.confirmationUrl) {
        window.top!.location.href = data.confirmationUrl;
      }
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/billing/cancel'),
  });

  if (statusLoading) {
    return (
      <Page title="Abonnement">
        <Box padding="800">
          <InlineStack align="center">
            <Spinner size="large" />
          </InlineStack>
        </Box>
      </Page>
    );
  }

  // Handle case where status is not loaded yet
  if (!status) {
    return (
      <Page title="Abonnement">
        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">
                  Chargement de votre abonnement...
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // Calculate usage percentage
  const usagePercent = status.shipmentsLimit > 0 
    ? Math.round((status.shipmentsThisMonth / status.shipmentsLimit) * 100) 
    : 0;
  const remaining = status.shipmentsLimit - status.shipmentsThisMonth;
  const isTrialing = (status.trialDaysRemaining || 0) > 0;

  return (
    <Page title="Abonnement">
      <Layout>
        {/* Current Plan */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="h2" variant="headingMd">
                    Plan actuel
                  </Text>
                  <InlineStack gap="200">
                    <Text as="span" variant="headingLg">
                      {status.planName}
                    </Text>
                    {isTrialing && (
                      <Badge tone="attention">{`${status.trialDaysRemaining || 0} jours d'essai restants`}</Badge>
                    )}
                  </InlineStack>
                </BlockStack>
                {status.planId === 'free' ? (
                  <Badge>Gratuit</Badge>
                ) : (
                  <Text as="span" variant="headingMd">
                    Actif
                  </Text>
                )}
              </InlineStack>

              <Divider />

              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="span">Expéditions ce mois</Text>
                  <Text as="span" fontWeight="semibold">
                    {status.shipmentsThisMonth} / {status.shipmentsLimit}
                  </Text>
                </InlineStack>
                <ProgressBar
                  progress={usagePercent}
                  tone={
                    usagePercent > 90
                      ? 'critical'
                      : usagePercent > 70
                      ? 'highlight'
                      : 'primary'
                  }
                />
                <Text as="span" tone="subdued" variant="bodySm">
                  {remaining > 0 ? `${remaining} expéditions restantes` : 'Limite atteinte'}
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Available Plans */}
        <Layout.Section>
          <Text as="h2" variant="headingMd">
            Plans disponibles
          </Text>
        </Layout.Section>

        {plans?.map((plan) => (
          <Layout.Section key={plan.id} variant="oneThird">
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">
                    {plan.name}
                  </Text>
                  {plan.popular && <Badge tone="success">Populaire</Badge>}
                </InlineStack>

                <InlineStack align="start" blockAlign="baseline" gap="100">
                  <Text as="span" variant="heading2xl">
                    {plan.price}€
                  </Text>
                  <Text as="span" tone="subdued">
                    /mois
                  </Text>
                </InlineStack>

                <Text as="p" tone="subdued">
                  {plan.shipmentsLimit.toLocaleString()} expéditions/mois
                </Text>

                <Divider />

                <BlockStack gap="200">
                  {plan.features.map((feature, index) => (
                    <InlineStack key={index} gap="200">
                      <Text as="span" tone="success">
                        ✓
                      </Text>
                      <Text as="span">{feature}</Text>
                    </InlineStack>
                  ))}
                </BlockStack>

                <Box paddingBlockStart="400">
                  {status.planId === plan.id ? (
                    <Button disabled fullWidth>
                      Plan actuel
                    </Button>
                  ) : (
                    <Button
                      variant={plan.popular ? 'primary' : undefined}
                      fullWidth
                      onClick={() => subscribeMutation.mutate(plan.id)}
                      loading={subscribeMutation.isPending}
                    >
                      {plan.price === 0
                        ? 'Passer au gratuit'
                        : 'Choisir ce plan'}
                    </Button>
                  )}
                </Box>
              </BlockStack>
            </Card>
          </Layout.Section>
        ))}

        {/* Cancel Section */}
        {status.planId !== 'free' && (
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Annuler l'abonnement
              </Text>
              <Text as="p" tone="subdued">
                Vous pouvez annuler votre abonnement à tout moment. Vous conserverez l'accès jusqu'à la fin de la période en cours.
              </Text>
              <Button
                tone="critical"
                onClick={() => cancelMutation.mutate()}
                loading={cancelMutation.isPending}
              >
                Annuler mon abonnement
              </Button>
            </BlockStack>
          </Card>
        )}
      </Layout>
    </Page>
  );
}
